import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';

// ── Context ───────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
    return ctx;
}

// ── Variant config ────────────────────────────────────────────────────────────
const VARIANTS = {
    success: { bg: '#d4edda', border: '#c3e6cb', color: '#155724', icon: '✅' },
    danger:  { bg: '#f8d7da', border: '#f5c6cb', color: '#721c24', icon: '❌' },
    warning: { bg: '#fff3cd', border: '#ffeeba', color: '#856404', icon: '⚠️'  },
    info:    { bg: '#d1ecf1', border: '#bee5eb', color: '#0c5460', icon: 'ℹ️'  },
};

// ── Provider ──────────────────────────────────────────────────────────────────
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);

    const showToast = useCallback((message, variant = 'success', duration = 4000) => {
        const id = nextId.current++;
        setToasts(prev => [...prev, { id, message, variant, duration }]);
    }, []);

    const dismiss = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {ReactDOM.createPortal(
                <ToastContainer toasts={toasts} onDismiss={dismiss} />,
                document.body
            )}
        </ToastContext.Provider>
    );
}

// ── Container ─────────────────────────────────────────────────────────────────
function ToastContainer({ toasts, onDismiss }) {
    if (toasts.length === 0) return null;
    return (
        <div style={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            pointerEvents: 'none',
        }}>
            {toasts.map(t => (
                <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
            ))}
        </div>
    );
}

// ── Individual toast ──────────────────────────────────────────────────────────
function ToastItem({ toast, onDismiss }) {
    const { id, message, variant, duration } = toast;
    const v = VARIANTS[variant] || VARIANTS.info;

    // Auto-dismiss
    React.useEffect(() => {
        const timer = setTimeout(() => onDismiss(id), duration);
        return () => clearTimeout(timer);
    }, [id, duration, onDismiss]);

    return (
        <div
            onClick={() => onDismiss(id)}
            style={{
                pointerEvents: 'auto',
                minWidth: 340,
                maxWidth: 560,
                background: v.bg,
                border: `1px solid ${v.border}`,
                borderRadius: 10,
                padding: '16px 20px',
                boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                cursor: 'pointer',
                animation: 'aims-toast-in 0.22s ease',
            }}
        >
            <span style={{ fontSize: '1.35em', lineHeight: 1.3 }}>{v.icon}</span>
            <span style={{ flex: 1, color: v.color, fontSize: '1em', lineHeight: 1.5, fontWeight: 500 }}>
                {message}
            </span>
            <span style={{ color: v.color, opacity: 0.55, fontSize: '1.1em', marginTop: -1, lineHeight: 1 }}>✕</span>
        </div>
    );
}

// ── Keyframe injection ────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('aims-toast-styles')) {
    const style = document.createElement('style');
    style.id = 'aims-toast-styles';
    style.textContent = `
        @keyframes aims-toast-in {
            from { opacity: 0; transform: translateY(-16px); }
            to   { opacity: 1; transform: translateY(0); }
        }
    `;
    document.head.appendChild(style);
}
